import { Button, Card, toast } from "@kkp/ui";
import { statusBadgeClass } from "@kkp/features";
import type { Company } from "../../../api/companies";

type CompaniesTable = ReturnType<
  typeof import("@kkp/features").useCompaniesTable
>;

export function ClientsTable({
  table,
  isLoading,
  onRowAction,
  onBulkAction,
}: {
  table: CompaniesTable;
  isLoading: boolean;
  onRowAction: (action: "view" | "edit" | "toggleStatus", c: Company) => void;
  onBulkAction: (action: "export" | "setActive" | "setInactive") => void;
}) {
  const {
    statusFilter,
    setStatusFilter,
    sortKey,
    sortDir,
    toggleSort,
    density,
    setDensity,
    resetFilters,
    selected,
    setSelected,
    selectedCount,
    isAllSelected,
    toggleAll,
    filtered,
    clearFilters,
    rowPadY,
  } = table;

  return (
    <Card className="p-0 overflow-hidden">
      <div className="border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
              Filters
            </div>

            <div className="flex flex-wrap gap-2">
              {(["All", "Active", "Inactive"] as const).map((s) => {
                const active = statusFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    className={[
                      "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                      active
                        ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                        : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                    ].join(" ")}
                    onClick={() => setStatusFilter(s)}
                    aria-pressed={active}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            <div className="h-5 w-px bg-[color:rgba(255,255,255,0.10)]" />

            <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
              Sort
            </div>

            <div className="flex flex-wrap gap-2">
              {(
                [
                  { key: "updatedAt", label: "Updated" },
                  { key: "name", label: "Name" },
                  { key: "status", label: "Status" },
                ] as const
              ).map((s) => {
                const active = sortKey === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    className={[
                      "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                      active
                        ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                        : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                    ].join(" ")}
                    onClick={() => toggleSort(s.key)}
                  >
                    {s.label}
                    {active ? (
                      <span className="ml-1 text-[11px] text-[color:rgba(255,255,255,0.65)]">
                        {sortDir.toUpperCase()}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
              Density
            </div>
            <div className="flex flex-wrap gap-2">
              {(["comfortable", "compact"] as const).map((d) => {
                const active = density === d;
                return (
                  <button
                    key={d}
                    type="button"
                    className={[
                      "inline-flex items-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                      active
                        ? "border-[color:rgba(255,255,255,0.18)] bg-[color:rgba(255,255,255,0.06)] text-white"
                        : "border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.02)] text-[color:var(--kkp-text-muted)] hover:text-white hover:bg-[color:rgba(255,255,255,0.05)]",
                    ].join(" ")}
                    onClick={() => setDensity(d)}
                  >
                    {d === "comfortable" ? "Comfortable" : "Compact"}
                  </button>
                );
              })}
            </div>

            <div className="h-5 w-px bg-[color:rgba(255,255,255,0.10)]" />

            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                resetFilters();
                toast.info("Filters reset.", {
                  title: "Filters",
                  durationMs: 1800,
                });
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      </div>

      {selectedCount > 0 ? (
        <div className="border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm font-semibold text-white">
              {selectedCount} selected
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onBulkAction("export")}
              >
                Export CSV
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onBulkAction("setActive")}
              >
                Set Active
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onBulkAction("setInactive")}
              >
                Set Inactive
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected({})}>
                Clear selection
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[color:var(--kkp-surface-2)]">
            <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
              <th className="w-[44px] px-4 py-3">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={toggleAll}
                  className="h-4 w-4 accent-[color:var(--kkp-magenta)]"
                  aria-label="Select all companies"
                />
              </th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Updated</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm text-[color:var(--kkp-text-muted)]"
                >
                  Loading companies…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm text-[color:var(--kkp-text-muted)]"
                >
                  No companies match your filters.
                  <div className="mt-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={clearFilters}
                    >
                      Clear filters
                    </Button>
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-[color:rgba(255,255,255,0.08)]"
                >
                  <td className={["px-4", rowPadY].join(" ")}>
                    <input
                      type="checkbox"
                      checked={Boolean(selected[row.id])}
                      onChange={(e) =>
                        setSelected((prev) => ({
                          ...prev,
                          [row.id]: e.target.checked,
                        }))
                      }
                      className="h-4 w-4 accent-[color:var(--kkp-magenta)]"
                      aria-label={`Select ${row.name}`}
                    />
                  </td>

                  <td className={["px-4", rowPadY].join(" ")}>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-[color:rgba(255,255,255,0.92)]">
                        {row.name}
                      </div>
                      <div className="mt-0.5 text-xs text-[color:var(--kkp-text-muted)]">
                        ID: {row.id}
                      </div>
                    </div>
                  </td>

                  <td className={["px-4", rowPadY].join(" ")}>
                    <span
                      className={[
                        "inline-flex items-center rounded-full border px-2.5 py-1",
                        "text-xs font-semibold text-white",
                        statusBadgeClass(row.status),
                      ].join(" ")}
                    >
                      {row.status}
                    </span>
                  </td>

                  <td
                    className={[
                      "px-4",
                      rowPadY,
                      "text-[color:var(--kkp-text-muted)]",
                    ].join(" ")}
                  >
                    {row.updatedAt}
                  </td>

                  <td className={["px-4", rowPadY].join(" ")}>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onRowAction("view", row)}
                      >
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRowAction("edit", row)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={row.status === "Active" ? "danger" : "success"}
                        onClick={() => onRowAction("toggleStatus", row)}
                      >
                        {row.status === "Active" ? "Deactivate" : "Activate"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
